// oxlint-disable no-console
/**
 * IndexedDB utilities for public event forms
 * This is a React-compatible version of the service worker IndexedDB utilities
 */

import Logger from 'utils/logger/Logger';

const LOG_PREFIX = 'PublicEventFormDB';
const verbose = false;

const log = (message: string, ...params: unknown[]): void => {
  if (verbose) {
    Logger.info(`${LOG_PREFIX}: ${message}`, ...params);
  }
};

class PublicEventFormDB {
  formsTableName = 'publicEventForm';

  submissionsTableName = 'publicEventSubmission';

  queueTableName = 'offlineRequests';

  lookupFieldsTableName = 'lookupFields';

  attachmentFilesTableName = 'attachmentFiles';

  dbName = 'VatixOfflineDB';

  version = 3;

  private db: IDBDatabase | null = null;

  private initPromise: Promise<IDBDatabase> | null = null;

  async init(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        log('IndexedDB opened successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        [
          [this.formsTableName, 'id'],
          [this.submissionsTableName, 'uuid'],
          [this.queueTableName, 'uuid'],
          [this.lookupFieldsTableName, 'id'],
          [this.attachmentFilesTableName, 'id'],
        ].forEach(([name, keyPath]) => {
          if (!db.objectStoreNames.contains(name)) {
            db.createObjectStore(name, { keyPath });
          }
        });
      };
    });

    return this.initPromise;
  }

  /**
   * Create a new submission record when a user starts filling a form
   */
  async createPublicEventSubmission(sessionId: string, formId: string): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.submissionsTableName], 'readwrite');
      const store = transaction.objectStore(this.submissionsTableName);

      const newSubmission = {
        uuid: sessionId,
        form_id: formId,
        content: {},
        structure: {},
        status: 'draft', // Initial state when user starts filling the form
        createdAt: new Date().toISOString(),
      };

      const request = store.put(newSubmission);

      request.onsuccess = () => {
        log('New submission record created:', newSubmission);
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to create submission record:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Sanitize data for IndexedDB storage by removing non-cloneable properties
   */
  private sanitizeForIndexedDB(data: unknown): unknown {
    if (data === null || data === undefined) {
      return data;
    }

    if (typeof data === 'function') {
      return null; // Remove functions
    }

    if (data instanceof Date) {
      return data.toISOString(); // Convert dates to strings
    }

    if (data instanceof RegExp) {
      return data.toString(); // Convert regex to strings
    }

    if (data instanceof Error) {
      return {
        name: data.name,
        message: data.message,
        stack: data.stack,
      };
    }

    if (Array.isArray(data)) {
      return data.map((item) => this.sanitizeForIndexedDB(item));
    }

    if (typeof data === 'object') {
      const sanitized: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        try {
          sanitized[key] = this.sanitizeForIndexedDB(value);
        } catch (error) {
          // Skip properties that can't be sanitized
          console.warn(`Skipping non-cloneable property: ${key}`, error);
        }
      }
      return sanitized;
    }

    return data;
  }

  /**
   * Update submission content as user fills the form
   */
  async updatePublicEventSubmission(
    sessionId: string,
    formId: string,
    data: {
      content: Record<string, unknown>;
      stack: string[];
      publicSubmissionDetails?: {
        name: string | null;
        email: string | null;
        phone: string | null;
      };
    }
  ): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.submissionsTableName], 'readwrite');
      const store = transaction.objectStore(this.submissionsTableName);

      // Sanitize the data before storing
      const sanitizedContent = this.sanitizeForIndexedDB(data.content) as Record<string, unknown>;
      const sanitizedStack = this.sanitizeForIndexedDB(data.stack) as string[];
      const sanitizedPublicSubmissionDetails = data.publicSubmissionDetails
        ? (this.sanitizeForIndexedDB(data.publicSubmissionDetails) as {
            name: string | null;
            email: string | null;
            phone: string | null;
          })
        : undefined;

      const updatedSubmission = {
        uuid: sessionId,
        form_id: formId,
        content: sanitizedContent,
        stack: sanitizedStack,
        publicSubmissionDetails: sanitizedPublicSubmissionDetails,
        status: 'draft', // Keep as draft while user is filling
      };

      const request = store.put(updatedSubmission);

      request.onsuccess = () => {
        log('Submission updated:', updatedSubmission);
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to update submission:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get submission by session ID
   */
  async getPublicEventSubmission(sessionId: string): Promise<unknown> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.submissionsTableName], 'readonly');
      const store = transaction.objectStore(this.submissionsTableName);
      const request = store.get(sessionId);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Store or update a public event form in IndexedDB
   */
  async upsertPublicEventForm(id: string, data: unknown): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.formsTableName], 'readwrite');
      const store = transaction.objectStore(this.formsTableName);

      // Sanitize the data before storing
      const sanitizedData = this.sanitizeForIndexedDB(data);

      const request = store.put({
        id,
        data: {
          ...(sanitizedData as Record<string, unknown>),
          hasNext: false,
        },
        timestamp: Date.now(),
      });

      request.onsuccess = () => {
        log(`Public event form stored: ${id}`);
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to store public event form:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Update submission status
   */
  async updatePublicEventSubmissionStatus(
    sessionId: string,
    status: 'draft' | 'pending' | 'submitted' | 'template_mismatch' | 'failed'
  ): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.submissionsTableName], 'readwrite');
      const store = transaction.objectStore(this.submissionsTableName);

      // First get the existing submission
      const getRequest = store.get(sessionId);

      getRequest.onsuccess = () => {
        if (getRequest.result) {
          // Sanitize the existing data before updating
          const sanitizedResult = this.sanitizeForIndexedDB(getRequest.result) as Record<string, unknown>;

          // Update the status
          const updatedSubmission = {
            ...sanitizedResult,
            status,
          };

          const putRequest = store.put(updatedSubmission);
          putRequest.onsuccess = () => {
            log(`Submission status updated: ${sessionId} to ${status}`);
            resolve();
          };
          putRequest.onerror = () => {
            console.error('Failed to update submission status:', putRequest.error);
            reject(putRequest.error);
          };
        } else {
          console.warn('Submission not found for status update:', sessionId);
          resolve(); // Don't fail if submission doesn't exist
        }
      };

      getRequest.onerror = () => {
        console.error('Failed to get submission for status update:', getRequest.error);
        reject(getRequest.error);
      };
    });
  }

  /**
   * Store lookup fields data for a form
   * Uses composite key: formId_type
   */
  async storeLookupFields(formId: string, type: string, fields: Array<{ name: string; uuid: string }>): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.lookupFieldsTableName], 'readwrite');
      const store = transaction.objectStore(this.lookupFieldsTableName);

      const lookupData = {
        id: `${formId}_${type}`,
        form_id: formId,
        created_at: new Date().toISOString(),
        type,
        fields,
      };

      const request = store.put(lookupData);

      request.onsuccess = () => {
        log(`Lookup fields stored for form: ${formId}_${type} type: ${type}`);
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to store lookup fields:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all draft submissions for a specific form
   */
  async getDraftSubmissionsByFormId(formId: string): Promise<
    Array<{
      uuid: string;
      form_id: string;
      content: Record<string, unknown>;
      status: string;
      createdAt: string;
    }>
  > {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.submissionsTableName], 'readonly');
      const store = transaction.objectStore(this.submissionsTableName);
      const request = store.getAll();

      request.onsuccess = () => {
        const allSubmissions = request.result || [];
        // Filter submissions by form_id and status 'draft'
        const draftSubmissions = allSubmissions.filter(
          (submission: { form_id: string; status: string }) =>
            submission.form_id === formId && submission.status === 'draft'
        );

        log(`Found ${draftSubmissions.length} draft submissions for form: ${formId}`);
        resolve(draftSubmissions);
      };

      request.onerror = () => {
        console.error('Failed to get draft submissions:', request.error);
        reject(request.error);
      };
    });
  }

  async deleteAllDraftSubmissionsByFormId(formId: string): Promise<void> {
    const drafts = await this.getDraftSubmissionsByFormId(formId);

    drafts.forEach((draft) => {
      this.deletePublicEventSubmission(draft.uuid);
    });
  }

  /**
   * Delete a specific submission by session ID
   */
  async deletePublicEventSubmission(sessionId: string): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.submissionsTableName], 'readwrite');
      const store = transaction.objectStore(this.submissionsTableName);
      const request = store.delete(sessionId);

      request.onsuccess = () => {
        log(`Submission deleted: ${sessionId}`);
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to delete submission:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * This helps clean up stale draft records that were never submitted
   */
  async deleteOldSubmissions(maxAgeDays: number = 7): Promise<number> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.submissionsTableName], 'readwrite');
      const store = transaction.objectStore(this.submissionsTableName);
      const request = store.getAll();

      request.onsuccess = async () => {
        const allSubmissions = request.result || [];
        const now = Date.now();
        const maxAgeMs = maxAgeDays * 24 * 60 * 60 * 1000;

        // Find old submissions to delete (only drafts older than maxAgeDays)
        const oldSubmissions = allSubmissions.filter((submission: { createdAt?: string; status: string }) => {
          if (!submission.createdAt) return false;
          const createdAt = new Date(submission.createdAt).getTime();
          const age = now - createdAt;
          // Only delete drafts that are old - don't delete in_queue or other statuses
          return age > maxAgeMs && submission.status === 'draft';
        });

        if (oldSubmissions.length === 0) {
          resolve(0);
          return;
        }

        // First, delete associated attachment files for each old submission in parallel
        const deleteAttachmentPromises = (oldSubmissions as Array<{ uuid: string }>).map((submission) =>
          this.deleteAllAttachmentFiles(submission.uuid).catch((error) => {
            console.warn('Failed to delete attachment files for old submission:', submission.uuid, error);
            // Continue with submission deletion even if attachment cleanup fails
          })
        );
        await Promise.all(deleteAttachmentPromises);

        // Now delete the submissions themselves
        const deleteTransaction = db.transaction([this.submissionsTableName], 'readwrite');
        const deleteStore = deleteTransaction.objectStore(this.submissionsTableName);

        let deleteCount = 0;
        const totalToDelete = oldSubmissions.length;

        oldSubmissions.forEach((submission: { uuid: string }) => {
          const deleteRequest = deleteStore.delete(submission.uuid);

          deleteRequest.onsuccess = () => {
            deleteCount += 1;
            if (deleteCount === totalToDelete) {
              log(`Cleaned up ${deleteCount} old draft submissions and their attachment files`);
              resolve(deleteCount);
            }
          };

          deleteRequest.onerror = () => {
            console.error('Failed to delete old submission:', deleteRequest.error);
            reject(deleteRequest.error);
          };
        });
      };

      request.onerror = () => {
        console.error('Failed to get submissions for cleanup:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete lookup fields data for a form
   * Deletes all lookup types for the specified form
   */
  async deleteLookupFields(formId: string): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.lookupFieldsTableName], 'readwrite');
      const store = transaction.objectStore(this.lookupFieldsTableName);

      // First, get all records for this form
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        const allRecords = getAllRequest.result || [];
        const formRecords = allRecords.filter((record: { form_id: string }) => record.form_id === formId);

        if (formRecords.length === 0) {
          resolve();
          return;
        }

        // Delete each record for this form
        let deleteCount = 0;
        const totalToDelete = formRecords.length;

        formRecords.forEach((record: { id: string }) => {
          const deleteRequest = store.delete(record.id);

          deleteRequest.onsuccess = () => {
            deleteCount += 1;
            if (deleteCount === totalToDelete) {
              log(`Lookup fields deleted for form: ${formId}`);
              resolve();
            }
          };

          deleteRequest.onerror = () => {
            console.error('Failed to delete lookup field record:', deleteRequest.error);
            reject(deleteRequest.error);
          };
        });
      };

      getAllRequest.onerror = () => {
        console.error('Failed to get lookup fields for deletion:', getAllRequest.error);
        reject(getAllRequest.error);
      };
    });
  }

  /**
   * Store an attachment file blob in IndexedDB
   */
  async storeAttachmentFile(sessionId: string, fieldKey: string, file: File): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.attachmentFilesTableName], 'readwrite');
      const store = transaction.objectStore(this.attachmentFilesTableName);

      const attachmentData = {
        id: `${sessionId}_${fieldKey}_${file.name}`,
        sessionId,
        fieldKey,
        filename: file.name,
        blob: file,
        size: file.size,
        type: file.type,
        createdAt: new Date().toISOString(),
      };

      const request = store.put(attachmentData);

      request.onsuccess = () => {
        log(`Attachment file stored: ${file.name} for session: ${sessionId}`);
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to store attachment file:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all attachment files for a session
   */
  async getAttachmentFiles(sessionId: string): Promise<Array<{ fieldKey: string; file: File }>> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.attachmentFilesTableName], 'readonly');
      const store = transaction.objectStore(this.attachmentFilesTableName);
      const request = store.getAll();

      request.onsuccess = () => {
        const allFiles = request.result || [];
        const sessionFiles = allFiles
          .filter(
            (record: { sessionId: string; fieldKey: string; blob: Blob; filename: string; type: string }) =>
              record.sessionId === sessionId
          )
          .map((record: { sessionId: string; fieldKey: string; blob: Blob; filename: string; type: string }) => ({
            fieldKey: record.fieldKey,
            file: new File([record.blob], record.filename, { type: record.type }),
          }));

        log(`Found ${sessionFiles.length} attachment files for session: ${sessionId}`);
        resolve(sessionFiles);
      };

      request.onerror = () => {
        console.error('Failed to get attachment files:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get attachment files for a specific field
   */
  async getFieldAttachmentFiles(sessionId: string, fieldKey: string): Promise<File[]> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.attachmentFilesTableName], 'readonly');
      const store = transaction.objectStore(this.attachmentFilesTableName);
      const request = store.getAll();

      request.onsuccess = () => {
        const allFiles = request.result || [];
        const fieldFiles = allFiles
          .filter(
            (record: { sessionId: string; fieldKey: string; blob: Blob; filename: string; type: string }) =>
              record.sessionId === sessionId && record.fieldKey === fieldKey
          )
          .map(
            (record: { sessionId: string; fieldKey: string; blob: Blob; filename: string; type: string }) =>
              new File([record.blob], record.filename, { type: record.type })
          );

        log(`Found ${fieldFiles.length} attachment files for field: ${fieldKey}`);
        resolve(fieldFiles);
      };

      request.onerror = () => {
        console.error('Failed to get field attachment files:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete a specific attachment file
   */
  async deleteAttachmentFile(sessionId: string, fieldKey: string, filename: string): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.attachmentFilesTableName], 'readwrite');
      const store = transaction.objectStore(this.attachmentFilesTableName);
      const fileId = `${sessionId}_${fieldKey}_${filename}`;
      const request = store.delete(fileId);

      request.onsuccess = () => {
        log(`Attachment file deleted: ${filename}`);
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to delete attachment file:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete all attachment files for a session
   */
  async deleteAllAttachmentFiles(sessionId: string): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.attachmentFilesTableName], 'readwrite');
      const store = transaction.objectStore(this.attachmentFilesTableName);
      const request = store.getAll();

      request.onsuccess = () => {
        const allFiles = request.result || [];
        const sessionFiles = allFiles.filter((record: { sessionId: string }) => record.sessionId === sessionId);

        if (sessionFiles.length === 0) {
          resolve();
          return;
        }

        let deleteCount = 0;
        const totalToDelete = sessionFiles.length;

        sessionFiles.forEach((record: { id: string }) => {
          const deleteRequest = store.delete(record.id);

          deleteRequest.onsuccess = () => {
            deleteCount += 1;
            if (deleteCount === totalToDelete) {
              log(`Deleted ${deleteCount} attachment files for session: ${sessionId}`);
              resolve();
            }
          };

          deleteRequest.onerror = () => {
            console.error('Failed to delete attachment file:', deleteRequest.error);
            reject(deleteRequest.error);
          };
        });
      };

      request.onerror = () => {
        console.error('Failed to get attachment files for deletion:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete all attachment files for a specific field
   */
  async deleteFieldAttachmentFiles(sessionId: string, fieldKey: string): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.attachmentFilesTableName], 'readwrite');
      const store = transaction.objectStore(this.attachmentFilesTableName);
      const request = store.getAll();

      request.onsuccess = () => {
        const allFiles = request.result || [];
        const fieldFiles = allFiles.filter(
          (record: { sessionId: string; fieldKey: string }) =>
            record.sessionId === sessionId && record.fieldKey === fieldKey
        );

        if (fieldFiles.length === 0) {
          resolve();
          return;
        }

        let deleteCount = 0;
        const totalToDelete = fieldFiles.length;

        fieldFiles.forEach((record: { id: string }) => {
          const deleteRequest = store.delete(record.id);

          deleteRequest.onsuccess = () => {
            deleteCount += 1;
            if (deleteCount === totalToDelete) {
              log(`Deleted ${deleteCount} attachment files for field: ${fieldKey}`);
              resolve();
            }
          };

          deleteRequest.onerror = () => {
            console.error('Failed to delete field attachment file:', deleteRequest.error);
            reject(deleteRequest.error);
          };
        });
      };

      request.onerror = () => {
        console.error('Failed to get field attachment files for deletion:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Check storage quota before storing large files
   */
  async checkStorageQuota(): Promise<{ used: number; available: number }> {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage || 0,
        available: estimate.quota || 0,
      };
    }
    return { used: 0, available: Infinity };
  }

  /**
   * Check if there's enough space to store a file
   */
  async canStoreFile(fileSize: number): Promise<boolean> {
    const { used, available } = await this.checkStorageQuota();
    const SAFETY_MARGIN = 50 * 1024 * 1024; // 50MB buffer
    return available - used - SAFETY_MARGIN > fileSize;
  }

  /**
   * Get all offline requests from the queue
   */
  async getOfflineRequests(): Promise<
    Array<{
      uuid: string;
      event_submission_id: string;
      request: {
        url: string;
        method: string;
        headers: Record<string, string>;
        body: string | null;
      };
    }>
  > {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.queueTableName], 'readonly');
      const store = transaction.objectStore(this.queueTableName);
      const request = store.getAll();

      request.onsuccess = () => {
        log(`Found ${request.result?.length || 0} offline requests`);
        resolve(request.result || []);
      };

      request.onerror = () => {
        console.error('Failed to get offline requests:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get offline queue items with their related submission data for display
   */
  async getOfflineQueueWithSubmissions(): Promise<
    Array<{
      uuid: string;
      event_submission_id: string;
      createdAt: string;
      submission: {
        uuid: string;
        form_id: string;
        status: string;
        createdAt?: string;
      } | null;
    }>
  > {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.queueTableName, this.submissionsTableName], 'readonly');
      const queueStore = transaction.objectStore(this.queueTableName);
      const submissionsStore = transaction.objectStore(this.submissionsTableName);

      const queueRequest = queueStore.getAll();

      queueRequest.onsuccess = async () => {
        const offlineRequests = queueRequest.result || [];

        if (offlineRequests.length === 0) {
          resolve([]);
          return;
        }

        const results = await Promise.all(
          offlineRequests.map(async (offlineReq: { uuid: string; event_submission_id: string; createdAt?: string }) => {
            const submissionRequest = submissionsStore.get(offlineReq.event_submission_id);

            return new Promise<{
              uuid: string;
              event_submission_id: string;
              createdAt: string;
              submission: {
                uuid: string;
                form_id: string;
                status: string;
                createdAt?: string;
              } | null;
            }>((resolveSubmission) => {
              submissionRequest.onsuccess = () => {
                resolveSubmission({
                  uuid: offlineReq.uuid,
                  event_submission_id: offlineReq.event_submission_id,
                  createdAt: offlineReq.createdAt || new Date().toISOString(),
                  submission: submissionRequest.result || null,
                });
              };
              submissionRequest.onerror = () => {
                resolveSubmission({
                  uuid: offlineReq.uuid,
                  event_submission_id: offlineReq.event_submission_id,
                  createdAt: offlineReq.createdAt || new Date().toISOString(),
                  submission: null,
                });
              };
            });
          })
        );

        log(`Found ${results.length} offline queue items with submissions`);
        resolve(results);
      };

      queueRequest.onerror = () => {
        console.error('Failed to get offline queue:', queueRequest.error);
        reject(queueRequest.error);
      };
    });
  }

  /**
   * Delete an offline request by UUID
   */
  async deleteOfflineRequest(uuid: string): Promise<void> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.queueTableName], 'readwrite');
      const store = transaction.objectStore(this.queueTableName);
      const request = store.delete(uuid);

      request.onsuccess = () => {
        log(`Offline request deleted: ${uuid}`);
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to delete offline request:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get count of pending offline requests
   */
  async getOfflineQueueCount(): Promise<number> {
    const db = await this.init();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.queueTableName], 'readonly');
      const store = transaction.objectStore(this.queueTableName);
      const request = store.count();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('Failed to count offline requests:', request.error);
        reject(request.error);
      };
    });
  }
}

// Export singleton instance
export const publicEventFormDB = new PublicEventFormDB();
